/**
 * useAssetInspections — PM-side query that GETs
 * /api/admin/assets/{asset_id}/inspections (Plan 01 endpoint, B1 fix).
 *
 * Returns InspectionRead[] ordered by saved_at DESC. The select() callback
 * unwraps the `{inspections: ...}` envelope so consumers receive the array
 * directly — TanStack Query memoizes around the wrapper to avoid retriggering
 * renders when only the wrapper identity changes.
 *
 * No `refetchInterval` here — the per-asset list refresh is mutation-driven
 * (PATCH inspection invalidates this key via onSuccess in useInspectionEdit;
 * a future "create inspection" mutation will do the same). Cross-PM-session
 * live updates and the SSE-driven invalidation lifecycle are Plan 05's
 * exclusive concern (B6+W4 scope boundary).
 *
 * `enabled: assetId > 0` short-circuits the query for the AssetSidePanel
 * placeholder render path (where the panel renders before a feature is
 * selected — never happens in practice today, but cheap defense).
 */
import { useQuery } from "@tanstack/react-query";
import { apiGet, ApiError } from "../../api/client";
import {
  ASSET_INSPECTIONS_KEY,
  type InspectionListResponse,
  type InspectionRead,
} from "../../api/inspections";

export function useAssetInspections(assetId: number) {
  return useQuery<InspectionListResponse, ApiError, InspectionRead[]>({
    queryKey: ASSET_INSPECTIONS_KEY(assetId),
    queryFn: () =>
      apiGet<InspectionListResponse>(`/api/admin/assets/${assetId}/inspections`),
    select: (resp) => resp.inspections,
    enabled: assetId > 0,
    staleTime: 30_000,
  });
}
